import { useEffect } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { CheckCircle2, CircleAlert, CircleX, Info, X, type LucideIcon } from "lucide-react"
import Animate, { AnimationTypes } from "../Animate/Animate"

export type SnackbarVariant = "success" | "info" | "warning" | "error"

export interface SnackbarItem {
  id: string
  title: string
  description?: string
  variant: SnackbarVariant
  durationMs: number
}

interface SnackbarProps {
  item?: SnackbarItem
  onClose: (id: string) => void
}

const variantStyles: Record<
  SnackbarVariant,
  {
    container: string
    icon: string
    close: string
    Icon: LucideIcon
  }
> = {
  success: {
    container: "border-emerald-300 bg-white text-slate-900",
    icon: "bg-emerald-600 text-white",
    close: "text-slate-500 hover:bg-emerald-50 hover:text-emerald-800",
    Icon: CheckCircle2,
  },
  info: {
    container: "border-sky-300 bg-white text-slate-900",
    icon: "bg-sky-600 text-white",
    close: "text-slate-500 hover:bg-sky-50 hover:text-sky-800",
    Icon: Info,
  },
  warning: {
    container: "border-amber-300 bg-white text-slate-900",
    icon: "bg-amber-500 text-white",
    close: "text-slate-500 hover:bg-amber-50 hover:text-amber-800",
    Icon: CircleAlert,
  },
  error: {
    container: "border-red-300 bg-white text-slate-900",
    icon: "bg-red-600 text-white",
    close: "text-slate-500 hover:bg-red-50 hover:text-red-800",
    Icon: CircleX,
  },
}

export const Snackbar = ({ item, onClose }: SnackbarProps) => {
  const { t } = useTranslation("general")

  useEffect(() => {
    if (!item || item.durationMs === 0) {
      return
    }

    const timeoutId = window.setTimeout(() => onClose(item.id), item.durationMs)

    return () => window.clearTimeout(timeoutId)
  }, [item, onClose])

  if (!item || typeof document === "undefined") {
    return null
  }

  const { Icon, container, icon, close } = variantStyles[item.variant]

  const isAssertive = item.variant === "error" || item.variant === "warning"

  return createPortal(
    <div className="pointer-events-none fixed inset-x-3 bottom-20 z-[10050] flex justify-center md:bottom-6">
      <Animate type={AnimationTypes.slideUpIn} duration="0.25s">
        <section
          key={item.id}
          className={`pointer-events-auto flex w-full max-w-[520px] items-start gap-3 rounded-lg border-2 p-3 pr-2 shadow-2xl ${container}`}
          role={isAssertive ? "alert" : "status"}
          aria-live={isAssertive ? "assertive" : "polite"}
          aria-atomic="true"
        >
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${icon}`}>
            <Icon size={20} aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1 self-center">
            <p className="break-words text-sm font-black leading-snug">{item.title}</p>

            {item.description && (
              <p className="mt-0.5 break-words text-sm font-semibold leading-snug text-slate-600">
                {item.description}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => onClose(item.id)}
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${close}`}
            aria-label={t("label_close_notification")}
            title={t("label_close_notification")}
            data-interaction-description-key="interactionDescriptions:components.snackbar.snackbar.button.close"
          >
            <X size={19} aria-hidden="true" />
          </button>
        </section>
      </Animate>
    </div>,
    document.body,
  )
}
